import { useState, useEffect } from 'react';
import { getServices, createService, updateService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Clock, DollarSign, Briefcase, Palette } from 'lucide-react';

const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#8B5CF6', // Violet
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#6366F1', // Indigo
  '#F97316', // Orange
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    default_duration_min: 60,
    default_rate_cents: 0,
    is_active: true,
    color: '#3B82F6',
  });

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (err) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleOpenSheet = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        default_duration_min: service.default_duration_min,
        default_rate_cents: service.default_rate_cents / 100,
        is_active: service.is_active,
        color: service.color || '#3B82F6',
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        default_duration_min: 60,
        default_rate_cents: 0,
        is_active: true,
        color: '#3B82F6',
      });
    }
    setSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        default_duration_min: parseInt(formData.default_duration_min),
        default_rate_cents: Math.round(parseFloat(formData.default_rate_cents) * 100),
        is_active: formData.is_active,
        color: formData.color,
      };

      if (editingService) {
        await updateService(editingService.id, data);
        toast.success('Service updated');
      } else {
        await createService(data);
        toast.success('Service created');
      }
      setSheetOpen(false);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save service');
    }
  };

  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

  const activeServices = services.filter((s) => s.is_active);
  const inactiveServices = services.filter((s) => !s.is_active);

  return (
    <div className="flex flex-col h-full" data-testid="services-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">Services</h1>

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button onClick={() => handleOpenSheet()} data-testid="new-service-btn">
              <Plus className="w-4 h-4 mr-2" /> New Service
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md">
            <SheetHeader>
              <SheetTitle>{editingService ? 'Edit Service' : 'New Service'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label>Service Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., 1-Hour Consultation"
                  required
                  data-testid="service-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Default Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.default_duration_min}
                  onChange={(e) => setFormData((p) => ({ ...p, default_duration_min: e.target.value }))}
                  min={5}
                  max={480}
                  required
                  data-testid="service-duration-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Default Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_cents}
                  onChange={(e) => setFormData((p) => ({ ...p, default_rate_cents: e.target.value }))}
                  min={0}
                  required
                  data-testid="service-rate-input"
                />
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Calendar Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full transition-all ${
                        formData.color === color 
                          ? 'ring-2 ring-offset-2 ring-primary scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData((p) => ({ ...p, color }))}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-sm text-muted-foreground">Custom:</Label>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))}
                    className="w-12 h-8 p-1 cursor-pointer"
                    data-testid="service-color-input"
                  />
                  <span className="text-sm font-mono text-muted-foreground">{formData.color}</span>
                </div>
              </div>

              {editingService && (
                <div className="flex items-center justify-between py-2">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData((p) => ({ ...p, is_active: checked }))}
                    data-testid="service-active-switch"
                  />
                </div>
              )}

              <Button type="submit" className="w-full" data-testid="save-service-btn">
                {editingService ? 'Update Service' : 'Create Service'}
              </Button>
            </form>
          </SheetContent>
        </Sheet>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-center text-muted-foreground py-12">Loading...</div>
        ) : services.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No services yet. Create your first service!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Services */}
            <div>
              <h2 className="text-lg font-medium mb-4">Active Services ({activeServices.length})</h2>
              {activeServices.length === 0 ? (
                <p className="text-muted-foreground">No active services</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeServices.map((service) => (
                    <Card 
                      key={service.id} 
                      className="hover:shadow-md transition-shadow relative overflow-hidden" 
                      data-testid={`service-card-${service.id}`}
                    >
                      {/* Color indicator bar */}
                      <div 
                        className="absolute top-0 left-0 right-0 h-1"
                        style={{ backgroundColor: service.color || '#3B82F6' }}
                      />
                      <CardHeader className="pb-2 pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: service.color || '#3B82F6' }}
                            />
                            <CardTitle className="text-lg">{service.name}</CardTitle>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenSheet(service)}
                            data-testid={`edit-service-${service.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{service.default_duration_min} min</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-mono">{formatMoney(service.default_rate_cents)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Inactive Services */}
            {inactiveServices.length > 0 && (
              <div>
                <h2 className="text-lg font-medium mb-4 text-muted-foreground">
                  Inactive Services ({inactiveServices.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {inactiveServices.map((service) => (
                    <Card key={service.id} className="opacity-60 hover:opacity-100 transition-opacity relative overflow-hidden">
                      <div 
                        className="absolute top-0 left-0 right-0 h-1 opacity-50"
                        style={{ backgroundColor: service.color || '#3B82F6' }}
                      />
                      <CardHeader className="pb-2 pt-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded-full opacity-50" 
                                style={{ backgroundColor: service.color || '#3B82F6' }}
                              />
                              <CardTitle className="text-lg">{service.name}</CardTitle>
                            </div>
                            <Badge variant="secondary" className="mt-1">Inactive</Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenSheet(service)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{service.default_duration_min} min</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-mono">{formatMoney(service.default_rate_cents)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
